import React, { createContext, use, useState, useCallback, useEffect, useRef, useMemo, ReactNode } from 'react';
import {
  type EFilingParty,
  type EFilingAttorney,
  type EFilingDocument,
  type TypedOrder,
  type OrderDocument,
  type CompletenessResult,
  type ValidationError,
  validateTab as sharedValidateTab,
  validateFeeCalcReadiness,
  canAssignDocTypes,
  canAssignPartyMetadata,
  calculateCompleteness,
  type DocumentMetadataItem,
  type OrderInfoState,
  type CaseInfoState,
  type CourtFeesState,
  type OrderDetailsState,
  type EFilingWizardState,
  type EFilingTab,
  type GuidedSection,
  type ExtractedPatch,
  mergeExtractedParties,
  mergeExtractedAttorneys,
  backfillRejectedCaseTypeName,
  backfillRejectedCategoryName,
  routeExtractedCaseCategory,
  routeExtractedCaseType,
  withDefaultSource,
  // Document storage-backing (SOR-375): the ONE fileName-keyed verdict, shared
  // with the server's write-time reconcile, readiness predicate and submit
  // guard. The wizard state carries no gcsPath — that column lives on the
  // order_documents row — so the hydrated draft reads backing from the order's
  // own rows rather than re-deriving it.
  backedFileNames,
  listedFileNames,
  unbackedListedFileNames,
  // Fee-quote resolvedness + the payment-method rule (SPEC-SOR-343): both live
  // in shared order-logic, and the portal composes them rather than re-deriving
  // either.
  isFeeQuoteResolved,
  validatePaymentMethod,
  // Assignment provenance (SPEC-SOR-220-v2): the ONE decider and the ONE
  // absence-resolving reader live in shared order-logic; the portal never
  // stamps a slot or resolves absence by hand.
  PARTY_CLASS_CODES,
  getAssignmentProvenance,
  nextAssignmentProvenance,
  type AssignmentProvenanceMap,
  type AssignmentProvenanceWrite,
} from '@lds-efsp/shared-contracts';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import { isSettledSubmitStatus } from '../lib/submit-outcome-routing';
import { resolveConfirmFlowFlag } from '../lib/confirm-flow-flag';
import { guardExtractionSections } from '../lib/extraction-section-guard';
import { revertCaseAttachSectionState } from '../lib/revert-case-attach-sections';
import { confirmSectionState } from '../lib/confirm-section';
import { buildSectionReadinessOptions } from '../lib/section-readiness-options';
// The zero fee shape has ONE literal, in the lib (SPEC-SOR-343) — the fresh
// draft spreads it rather than holding a second hand-maintained copy. The
// QuoteState -> absent transition (SPEC-SOR-363): the projection of the
// fee-determining inputs and the snapshot-pair decider live in the same fee
// module beside the record they invalidate.
import {
  ZERO_FEES,
  clearedFeeRecordPatch,
  feeInputFingerprint,
  feeQuoteInvalidationPatch,
} from '../lib/fee-quote-record';
import { classifySubmitRefusal } from '../lib/submit-refusal';
// Aliased: the context MEMBER is named `offendingFieldAria` too (SPEC-SOR-321
// R8) — an unaliased import would shadow it.
import {
  offendingFieldAria as computeOffendingFieldAria,
  type OffendingFieldAria,
} from '../lib/offending-fields';
import {
  saveIndicatorState,
  type SaveOutcome,
  type SaveIndicatorState,
} from '../lib/save-indicator';

const DRAFT_STORAGE_KEY = 'efilingDraft';

// Undelivered-submit reconcile window (SPEC-SOR-79-v1): the server commit
// happens inside the request whose response was lost (observed up to ~7.4s
// server-side on the 2026-07-06 Santa Cruz run), so a reconcile GET fired
// immediately can read `draft` before that transaction commits. Only a
// `draft` observed on the FINAL attempt of this bounded window concludes
// rollback.
const RECONCILE_MAX_ATTEMPTS = 3;
const DEFAULT_RECONCILE_RETRY_DELAY_MS = 1500;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// "Is a reference" — the repo's ONE reference shape: a NON-EMPTY array of
// strings under metadataValues[code], the identical guard
// `getAssignmentProvenance`, `referencesResolve` and the remap's all-strings
// filter apply. An empty array, a scalar codeList value and a new-data object
// array are not references.
const holdsReference = (v: unknown): boolean =>
  Array.isArray(v) && v.length > 0 && v.every(x => typeof x === 'string');

export type ExtractionStatus = 'idle' | 'extracting' | 'extracted' | 'failed';

// Re-export state types so existing component imports continue to work
export type {
  OrderInfoState,
  CaseInfoState,
  CourtFeesState,
  OrderDetailsState,
  EFilingWizardState,
  EFilingTab,
};

// Tab validation status
export interface TabValidation {
  'order-info': boolean;
  'case-details': boolean;
  'confirm': boolean;
  'documents': boolean;
  'court-fees': boolean;
  'order-details': boolean;
}

const defaultOrderInfo: OrderInfoState = {
  state: 'CA',
  county: '',
  filingType: '',
  serveAfterFiling: false,
  eServeCaseParticipants: false,
  isFirstAppearance: false,
};

const defaultCaseInfo: CaseInfoState = {
  caseTitle: '',
  caseCategoryCode: '',
  caseCategoryName: '',
  // OCR-recovery state, written only by applyExtraction (SOR-173). Spelled out
  // rather than left absent so every construct/reset site is greppable and a
  // rejected category name or a stale candidate list can never be inherited.
  caseCategoryCandidates: undefined,
  ocrExtractedCategoryName: undefined,
  ocrExtractedCaseTypeName: undefined,
  caseType: null,
  caseSubType: null,
  jurisdictionalAmount: '',
  courtJurisdiction: null,
  incidentZip: '',
  trialCourtCaseNumber: '',
  caseTrackingId: '',
  complaintId: '',
  complaintTitle: '',
  subcaseCount: 0,
  caseSourceCounty: '',
  additionalInfo: {
    conditionallyUnderSeal: false,
    confidentialCase: false,
    complexCase: false,
    classActionIndicator: false,
  },
  demandAmount: 0,
  premisesAddress: {},
  caseTypeMetadata: null,
};

/**
 * The fresh-draft fee state (SPEC-SOR-343): no calculation has run, so it
 * carries NO quote record — `quoteState` and `quote` are deliberately absent,
 * which reads as `absent` through the shared contract. The record itself is
 * written only by CourtFeesTab through `feeRecordPatch` (the resolved face
 * writes it; every other outcome clears it with an explicit `quote: undefined`
 * key), never here. Spelling either key here would clobber a hydrated draft's
 * record through the `{ ...defaultWizardState, ...typeData }` spread — the same
 * absent-is-nothing discipline the persisted `sectionStatuses` map follows.
 */
const defaultCourtFees: CourtFeesState = {
  fees: { ...ZERO_FEES },
  authorizePayment: false,
};

const defaultOrderDetails: OrderDetailsState = {
  serviceLevel: '',
  messageToClerk: '',
  paymentMethodId: '',
  internalReferenceNumber: '',
};

const defaultWizardState: EFilingWizardState = {
  orderInfo: defaultOrderInfo,
  caseInfo: defaultCaseInfo,
  parties: [],
  attorneys: [],
  documents: [],
  courtFees: defaultCourtFees,
  orderDetails: defaultOrderDetails,
  ocrConfirmedFields: [],
  ocrPopulatedFields: [],
};

/**
 * SOR-173: route a legacy draft's court-rejected category name onto the field
 * reserved for quoting it, in place, on the hydrated state.
 *
 * Drafts persisted before the routing rule landed hold the rejected name in
 * `caseCategoryName`, which every category display surface renders as the
 * filing's category while `categoryNotRecognized` — which now reads the
 * DECLARED field — leaves the recovery banner and its candidate chips
 * unmounted. That is strictly worse than pre-change, where the predicate
 * re-derived the rejection from the name and the recovery did fire. Both
 * hydration paths (localStorage resume, server `type_data` load) run this, so
 * legacy and freshly-extracted drafts reach every reader in the same shape;
 * the wizard's own save then re-persists the normalized values.
 */
function applyCategoryBackfill(state: EFilingWizardState): void {
  const backfilled = backfillRejectedCategoryName(state.caseInfo, state.ocrPopulatedFields);
  state.caseInfo = backfilled.caseInfo;
  state.ocrPopulatedFields = backfilled.ocrPopulatedFields;
}

/**
 * SOR-194: the same move for a legacy draft's court-rejected case TYPE name,
 * which the pre-change write site parked in `caseType.name` beside an empty
 * code. Read back unchanged, the confirm card and the Filing Summary state that
 * name as the filing's type directly above the picker explaining they could not
 * read it. Runs at the same hydration chokepoint and for the same reason as the
 * category backfill above; the two are independent and either may fire alone.
 */
function applyCaseTypeBackfill(state: EFilingWizardState): void {
  const backfilled = backfillRejectedCaseTypeName(state.caseInfo, state.ocrPopulatedFields);
  state.caseInfo = backfilled.caseInfo;
  state.ocrPopulatedFields = backfilled.ocrPopulatedFields;
}

interface EFilingContextValue {
  // Current tab
  currentTab: EFilingTab;
  setCurrentTab: (tab: EFilingTab) => void;
  // Wizard state
  wizardState: EFilingWizardState;
  // Tab-specific updaters
  updateOrderInfo: (updates: Partial<OrderInfoState>) => void;
  updateCaseInfo: (updates: Partial<CaseInfoState>) => void;
  setParties: (parties: EFilingParty[]) => void;
  addParty: (party: EFilingParty) => void;
  removeParty: (partyId: string) => void;
  updateParty: (partyId: string, updates: Partial<EFilingParty>) => void;
  setAttorneys: (attorneys: EFilingAttorney[]) => void;
  addAttorney: (attorney: EFilingAttorney) => void;
  removeAttorney: (attorneyId: string) => void;
  setDocuments: (documents: EFilingDocument[]) => void;
  addDocument: (doc: EFilingDocument) => void;
  removeDocument: (docId: string) => void;
  updateDocument: (docId: string, updates: Partial<EFilingDocument>) => void;
  /**
   * Functionally merge a document's `metadataValues` against the LATEST committed
   * state (not a render-time snapshot). Callers that precompute a merged object
   * from a closure-captured `documents` snapshot can silently drop a value set by
   * a concurrent update in the same render window (SOR-209: a user-toggled boolean
   * reverting after tab navigation). Routing every metadata mutation through this
   * updater guarantees the merge reads `prev` and preserves user-set entries.
   */
  /**
   * The ONE writer of per-document metadata. `write` states HOW the write came
   * to be (SPEC-SOR-220-v2) so each touched party-class slot's provenance is
   * derived through the shared decider; it defaults to `'filer'` — the filer's
   * own control act — because every legacy call site is exactly that.
   */
  updateDocumentMetadata: (
    docId: string,
    updater: (prev: Record<string, unknown>) => Record<string, unknown>,
    write?: AssignmentProvenanceWrite,
  ) => void;
  updateCourtFees: (updates: Partial<CourtFeesState>) => void;
  /**
   * The fee-input fingerprint of the LATEST committed draft (SPEC-SOR-363) —
   * the same baseline the quote-invalidation effect compares against, read at
   * call time through the provider's ref so an async caller sees the current
   * value rather than the snapshot its own closure captured. `null` only
   * before the provider's first observation.
   */
  getFeeInputsFingerprint: () => string | null;
  updateOrderDetails: (updates: Partial<OrderDetailsState>) => void;
  // Validation & completeness
  tabValidation: TabValidation;
  validateTab: (tab: EFilingTab) => boolean;
  getTabValidationErrors: (tab: EFilingTab) => ValidationError[];
  /**
   * Strict envelope readiness for fee calculation (Walter 2026-07-10 R7): the
   * documents-tab rules with NO document-first deferrals, plus required-metadata
   * checks against the registered doc-type policy. Non-empty = fee-calc (and
   * the Review Fees step) must not be entered; each error names the offending
   * document/field in plain language.
   */
  getFeeCalcBlockers: () => ValidationError[];
  /**
   * The R8 attribute pair for one control (`offendingFieldAria(controlId)`),
   * derived from the CURRENT tab's validation errors — the validation banner's
   * items are the canonical id space, so an `aria-describedby` reference set
   * here resolves in the same render and drops the moment no item names the
   * field. Curried to the control id; consumers spread the result.
   */
  offendingFieldAria: (controlId: string) => OffendingFieldAria;
  /**
   * Publish the per-doc-type required-metadata items from the Documents step's
   * policy fetch, so validation can enforce required metadata outside that
   * component (the items aren't part of wizard state).
   */
  registerDocTypeMetadata: (map: Record<string, DocumentMetadataItem[]>) => void;
  /**
   * The published per-doc-type metadata items (read side of
   * `registerDocTypeMetadata`). Lets steps other than Upload render per-doc
   * assignment controls — the About Your Case document-assignment section
   * (Walter 2026-07-10 R20) reads FILING_PARTY / FILING_ATTORNEY items here.
   */
  docTypeMetadata: Record<string, DocumentMetadataItem[]>;
  completeness: CompletenessResult;
  // Draft management
  /**
   * Mirror the wizard state into the localStorage draft slot. Reports whether
   * the write actually persisted (false when storage is unavailable or
   * quota-exceeded) so a save path whose localStorage leg is its WHOLE save
   * can record the truth (SOR-355); the server-save paths call it as a mirror
   * and record from the server outcome instead.
   */
  saveDraft: () => boolean;
  loadDraft: () => boolean;
  /** Fetch a draft (or ready-for-review) order from the server and hydrate the wizard (Edit Draft / ?orderId= resume / markReadyForReview review deep-link). Returns false if the order isn't loadable. */
  loadDraftFromServer: (draftOrderId: string) => Promise<boolean>;
  clearDraft: () => void;
  /**
   * Reset the wizard for a fresh-start entry (SOR-294 / R2): clear the in-memory
   * session to the unbound default AND overwrite the persisted draft slot to that
   * default unbound state, so a stale draft can never be resumed. Mirrors
   * `clearDraft`'s reset but writes the default slot synchronously instead of
   * merely removing it.
   */
  startFreshDraft: () => void;
  /**
   * SOR-355 / R17 — the autosave indicator state, derived from the last save
   * outcome and whether the order changed since it. `saved` holds until the
   * order state changes; nothing reverts it on a timer. The breadcrumb badge
   * and the Filing Summary rail both render from this one value — the truth
   * table lives only in `lib/save-indicator.ts`.
   */
  saveIndicator: SaveIndicatorState;
  // Navigation helpers
  canProceed: (tab: EFilingTab) => boolean;
  nextTab: () => void;
  previousTab: () => void;
  // Computed
  effectiveOrderType: string;
  showCourtFeesTab: boolean;
  /**
   * True when the consolidated "Confirm your filing" flow is active
   * (?confirmFlow=1 → localStorage; Walter R49 phase 1). Resolved once per
   * wizard mount. The wizard + stepper render the four-step defs when set.
   */
  confirmFlowEnabled: boolean;
  // API integration
  orderId: string | null;
  isSubmitting: boolean;
  submitError: string | null;
  /**
   * True when a submit attempt's outcome could not be determined: the response
   * was lost AND the reconcile fetches never reached the server. While set,
   * Submit must stay disabled — a second submit while the first may have
   * landed could double-file. Retry is offered only after a reconcile
   * confirmed the order is still `draft` (which sets submitError instead).
   */
  submitOutcomeUnknown: boolean;
  createDraftOrder: () => Promise<string>;
  saveDraftToServer: () => Promise<void>;
  submitOrder: () => Promise<TypedOrder>;
  uploadDocument: (file: File, category?: string) => Promise<OrderDocument>;
  // Upload tracking
  uploadStatuses: Record<string, 'uploading' | 'uploaded' | 'failed'>;
  allUploadsComplete: boolean;
  retryUpload: (docId: string, file: File) => Promise<void>;
  // OCR extraction
  extractionStatus: ExtractionStatus;
  setExtractionStatus: (status: ExtractionStatus) => void;
  ocrPopulatedFields: Set<string>;
  applyExtraction: (patch: ExtractedPatch) => void;
  /**
   * Mark an OCR confirmation card as user-verified ("Looks right"); persisted
   * with the draft.
   *
   * SPEC-SOR-174 R23 subordinates this record rather than deleting it: it keeps
   * its writer and its persistence, and loses only its say in a gate. No Next
   * gate and no Submit gate reads `ocrConfirmedFields` — `confirmSection` below
   * is the confirmation the gates consult.
   */
  confirmOcrField: (cardId: string) => void;
  /**
   * SPEC-SOR-174 R28 — the filer's confirm act on one guided section, and the
   * ONLY writer of a `confirmed` status anywhere in the wizard.
   *
   * Readiness never promotes a section: a draft that arrives already complete,
   * at first load or after rehydration, is no closer to confirmed than an empty
   * one, so the filer is still asked to approve all four. Every other writer of
   * `sectionStatuses` can reach `pending` or `prefilled` only.
   */
  confirmSection: (section: GuidedSection) => void;
  /**
   * SPEC-SOR-174 R17: return every guided section to `pending` and discard the
   * filer's confirm act on all of them. Called from the court-change reset
   * chokepoint — an approval granted for the previous court is not an approval
   * of what replaced it.
   */
  resetSectionStatuses: () => void;
  /**
   * SPEC-SOR-174 R18: return the case, parties and documents sections to
   * `pending` and discard the filer's confirm act on those three, leaving the
   * court section exactly as it stands. Called from the case-attach handler —
   * an approval granted for the previous CASE is not an approval of the one
   * that replaced it, but the filing court did not change, so `sec_court` keeps
   * both its status and its provenance.
   */
  revertCaseAttachSections: () => void;
  // Reference-data prefetch — populated when the county becomes known so the
  // Case Details step renders categories without a cold GetPolicy round-trip.
  prefetchedCategories: Record<string, RefOption[]>;
}

/** Minimal case-category reference option (mirrors CaseInfoTab's RefOption). */
interface RefOption {
  code: string;
  name: string;
  efmCode?: string;
}

const EFilingContext = createContext<EFilingContextValue | null>(null);

// Keep these id orders in sync with the tab defs in EFilingWizard.tsx — navigation and the stepper drift apart otherwise.
const CLASSIC_TAB_ORDER: EFilingTab[] = [
  'documents',
  'order-info',
  'case-details',
  'court-fees',
  'order-details',
];

// Consolidated flow (Walter R49 phase 1): Where to File + About Your Case
// merge into the single 'confirm' step. Flag-gated via ?confirmFlow=1
// (lib/confirm-flow-flag.ts); classic order stays the default.
const CONFIRM_TAB_ORDER: EFilingTab[] = [
  'documents',
  'confirm',
  'court-fees',
  'order-details',
];

interface EFilingProviderProps {
  children: ReactNode;
  /**
   * Spacing between reconcile attempts after an undelivered submit response.
   * Injectable so tests drive the bounded-retry window deterministically.
   */
  reconcileRetryDelayMs?: number;
}

export const EFilingProvider: React.FC<EFilingProviderProps> = ({
  children,
  reconcileRetryDelayMs = DEFAULT_RECONCILE_RETRY_DELAY_MS,
}) => {
  const [currentTab, setCurrentTab] = useState<EFilingTab>('documents');
  // Resolved once per wizard mount (useState initializer): mid-session flag
  // flips can't strand currentTab on a tab the active order doesn't contain.
  // currentTab is never draft-persisted, so a remount always re-enters at
  // 'documents' regardless of which flow produced the draft.
  const [confirmFlowEnabled] = useState<boolean>(() => resolveConfirmFlowFlag());
  const tabOrder = confirmFlowEnabled ? CONFIRM_TAB_ORDER : CLASSIC_TAB_ORDER;
  // Stamped with the active flow at birth (SPEC-SOR-174 R30) so the very first
  // save already tells the server which flow produced the draft;
  // `normalizeHydratedState` below re-stamps whenever a hydration path replaces
  // this state wholesale.
  const [wizardState, setWizardState] = useState<EFilingWizardState>(() =>
    confirmFlowEnabled ? { ...defaultWizardState, confirmFlowActive: true } : defaultWizardState,
  );

  // API integration state
  const [orderId, setOrderId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOutcomeUnknown, setSubmitOutcomeUnknown] = useState(false);

  // SOR-355 / R17 — the last save outcome AND the `wizardState` reference it
  // persisted, held as one state pair so they move atomically. "Changed since
  // save" is reference identity: every writer produces a new object through
  // `setWizardState`, so comparing the saved reference needs no version
  // counter and no wiring into individual writers. The deliberate
  // identity-return writers (`confirmOcrField`/`confirmSection` on a no-op
  // confirm) preserve the reference, so a no-op confirm act does not flip a
  // saved indicator to unsaved. State, not a ref, so the memo below reads only
  // render-stable values.
  const [lastSave, setLastSave] = useState<{ outcome: SaveOutcome; savedRef: EFilingWizardState | null }>({
    outcome: 'none',
    savedRef: null,
  });
  const saveIndicator = useMemo(
    () =>
      saveIndicatorState({
        lastSaveOutcome: lastSave.outcome,
        changedSinceSave: wizardState !== lastSave.savedRef,
      }),
    [wizardState, lastSave],
  );
  // Saves complete out of order: only the autosave leg is guarded against
  // overlapping (savePendingRef below), so a server autosave that started
  // BEFORE a manual Save Draft can settle AFTER it. Each attempt therefore
  // stamps a sequence when it starts, and a completion records only if it is
  // not older than the one already recorded — the newer save's outcome wins,
  // and the stale completion cannot flip a genuinely saved draft back to
  // "Not saved" until the next save. Refs, not state: sequencing is
  // bookkeeping between attempt start and completion, never rendered.
  const saveSeqRef = useRef(0);
  const lastRecordedSaveSeqRef = useRef(0);
  const recordSave = useCallback(
    (seq: number, outcome: SaveOutcome, savedRef: EFilingWizardState | null) => {
      if (seq < lastRecordedSaveSeqRef.current) return;
      lastRecordedSaveSeqRef.current = seq;
      setLastSave({ outcome, savedRef });
    },
    [],
  );

  // Upload tracking: docId → status
  const [uploadStatuses, setUploadStatuses] = useState<Record<string, 'uploading' | 'uploaded' | 'failed'>>({});
  const pendingFilesRef = useRef<Map<string, File>>(new Map());

  // OCR extraction state
  const [extractionStatus, setExtractionStatus] = useState<ExtractionStatus>('idle');
  // Derived from the persisted draft so the OCR confirmation cards re-render
  // from a loaded draft after a hard reload, not just in-session (WOL-329).
  const ocrPopulatedFields = useMemo(
    () => new Set(wizardState.ocrPopulatedFields ?? []),
    [wizardState.ocrPopulatedFields],
  );
  const { get, post, patch, del, fetchWithAuth, isAuthenticated } = useApi();
  const savePendingRef = useRef(false);
  // Suppresses the single auto-save that fires right after a server hydration.
  // Without it, opening a ready-for-review order via the markReadyForReview
  // review deep-link would PATCH the just-loaded state back after the debounce,
  // reopening it to draft (R-EDIT-REOPEN) before any user edit. The next genuine
  // edit saves normally.
  const skipNextServerSaveRef = useRef(false);

  // The e-filing wizard only ever produces e-filing orders (SOR-155 removed the
  // Federal option that derived a 'court-filing' order type).
  const effectiveOrderType = 'e-filing';

  // Court fees always apply to an e-filing (SOR-155). Whether a county is
  // selected yet must NOT factor in (Walter R46): gating on county made the
  // stepper show 4 steps on Upload Documents and jump to 5 once a county was
  // picked on Where to File. The step is simply not-yet-reachable before then.
  const showCourtFeesTab = true;

  // Tab-specific updaters
  const updateOrderInfo = useCallback((updates: Partial<OrderInfoState>) => {
    setWizardState(prev => ({
      ...prev,
      orderInfo: { ...prev.orderInfo, ...updates },
    }));
  }, []);

  const updateCaseInfo = useCallback((updates: Partial<CaseInfoState>) => {
    setWizardState(prev => ({
      ...prev,
      caseInfo: { ...prev.caseInfo, ...updates },
    }));
  }, []);

  const confirmOcrField = useCallback((cardId: string) => {
    setWizardState(prev => {
      const current = prev.ocrConfirmedFields ?? [];
      if (current.includes(cardId)) return prev;
      return { ...prev, ocrConfirmedFields: [...current, cardId] };
    });
  }, []);

  // SPEC-SOR-174 R28 — the sole writer of a `confirmed` status. The transition
  // itself is pure and lives in the lib beside the R17 court-change wipe and
  // the R18 case-change revert, where it is testable over the whole status ×
  // provenance space; this callback is the `setWizardState` around it. The
  // identity return on an already-confirmed section makes a double-click a
  // genuine no-op rather than a redundant state write.
  const confirmSection = useCallback((section: GuidedSection) => {
    setWizardState(prev => {
      const next = confirmSectionState(prev, section);
      return next === prev ? prev : { ...prev, ...next };
    });
  }, []);

  // SPEC-SOR-174 R17. Both fields are dropped rather than spelled out as four
  // explicit `pending` entries: an absent map IS every section pending (the
  // sole reader is the shared `getSectionStatus`), which is exactly the shape
  // `defaultWizardState` and a legacy draft carry, so there is one
  // representation of "nothing confirmed here" instead of two.
  const resetSectionStatuses = useCallback(() => {
    setWizardState(prev => ({
      ...prev,
      sectionStatuses: undefined,
      filerConfirmedSections: undefined,
    }));
  }, []);

  // SPEC-SOR-174 R18 — the case-change analogue of the R17 reset above, and
  // deliberately PARTIAL: only the case, parties and documents sections are
  // voided, so the court section keeps both its status and its provenance. The
  // transition itself is pure and lives in the lib beside the court-change
  // wipe, where it is testable over the `confirmed` states only the confirm
  // control can produce.
  const revertCaseAttachSections = useCallback(() => {
    setWizardState(prev => ({ ...prev, ...revertCaseAttachSectionState(prev) }));
  }, []);

  const setParties = useCallback((parties: EFilingParty[]) => {
    setWizardState(prev => ({ ...prev, parties }));
  }, []);

  const addParty = useCallback((party: EFilingParty) => {
    setWizardState(prev => ({ ...prev, parties: [...prev.parties, party] }));
  }, []);

  const removeParty = useCallback((partyId: string) => {
    setWizardState(prev => ({
      ...prev,
      parties: prev.parties.filter(p => p.id !== partyId),
    }));
  }, []);

  const updateParty = useCallback((partyId: string, updates: Partial<EFilingParty>) => {
    setWizardState(prev => ({
      ...prev,
      parties: prev.parties.map(p => p.id === partyId ? { ...p, ...updates } : p),
    }));
  }, []);

  const setAttorneys = useCallback((attorneys: EFilingAttorney[]) => {
    setWizardState(prev => ({ ...prev, attorneys }));
  }, []);

  const addAttorney = useCallback((attorney: EFilingAttorney) => {
    setWizardState(prev => ({ ...prev, attorneys: [...prev.attorneys, attorney] }));
  }, []);

  const removeAttorney = useCallback((attorneyId: string) => {
    setWizardState(prev => ({
      ...prev,
      attorneys: prev.attorneys.filter(a => a.id !== attorneyId),
    }));
  }, []);

  const setDocuments = useCallback((documents: EFilingDocument[]) => {
    setWizardState(prev => ({ ...prev, documents }));
  }, []);

  const addDocument = useCallback((doc: EFilingDocument) => {
    setWizardState(prev => ({ ...prev, documents: [...prev.documents, doc] }));
  }, []);

  const removeDocument = useCallback((docId: string) => {
    const isLastDocument =
      wizardState.documents.length === 1 &&
      wizardState.documents[0]?.id === docId;
    setWizardState(prev => {
      const documents = prev.documents.filter(d => d.id !== docId);
      if (documents.length > 0) return { ...prev, documents };
      // Last document removed — drop OCR-extracted state so a fresh upload starts clean.
      return {
        ...prev,
        documents,
        parties: [],
        attorneys: [],
        orderInfo: { ...prev.orderInfo, county: '', filingType: '' },
        caseInfo: {
          ...prev.caseInfo,
          caseCategoryCode: '',
          caseCategoryName: '',
          // SOR-173/SOR-194: all three are extraction-derived, so they go with
          // the rest of the OCR state — a rejected category or type name, or a
          // candidate set, from the discarded document must not outlive it.
          caseCategoryCandidates: undefined,
          ocrExtractedCategoryName: undefined,
          ocrExtractedCaseTypeName: undefined,
          caseType: null,
          caseTitle: '',
          incidentZip: '',
        },
        ocrPopulatedFields: [],
        ocrConfirmedFields: [],
      };
    });
    if (isLastDocument) setExtractionStatus('idle');
    // Best-effort server delete so the order_documents row (which order-details
    // renders) and its GCS object don't linger as a "ghost" after removal. A
    // not-yet-uploaded doc has no server row, so the DELETE 404s harmlessly; the
    // local-state drop above is the user-visible effect either way.
    if (orderId) {
      del(`${API_BASE_URL}/api/orders/${orderId}/documents/${docId}`).catch(() => {});
    }
  }, [wizardState.documents, setExtractionStatus, del, orderId]);

  const updateDocument = useCallback((docId: string, updates: Partial<EFilingDocument>) => {
    setWizardState(prev => ({
      ...prev,
      documents: prev.documents.map(d => d.id === docId ? { ...d, ...updates } : d),
    }));
  }, []);

  const updateDocumentMetadata = useCallback(
    (
      docId: string,
      updater: (prev: Record<string, unknown>) => Record<string, unknown>,
      // SPEC-SOR-220-v2: how this write came to be. Optional with a `'filer'`
      // default, so no existing call site changes — and every legacy call site
      // (the selector/checkbox writes) IS a filer act. The gap-fill and
      // case-change kinds are passed explicitly at their sites.
      write: AssignmentProvenanceWrite = 'filer',
    ) => {
      setWizardState(prev => ({
        ...prev,
        documents: prev.documents.map(d => {
          if (d.id !== docId) return d;
          const prevValues = d.metadataValues || {};
          const nextValues = updater(prevValues);
          // Derive each touched party-class slot's next provenance through the
          // ONE decider and persist the map BESIDE metadataValues, so it rides
          // orders.type_data and the localStorage slot and an Edit-Draft reload
          // keeps it. Only party-class codes are tracked — the provenance
          // vocabulary covers exactly PARTY_CLASS_CODES — and reference inequality
          // (nextValues[code] !== prevValues[code]) is precise for "touched":
          // untouched keys of the spread keep their identity, so a rewrite of
          // some other code never re-litigates a slot.
          const stamped: AssignmentProvenanceMap = {};
          let stampedAny = false;
          for (const code of PARTY_CLASS_CODES) {
            if (nextValues[code] === prevValues[code]) continue;
            // A case-change clear may stamp ONLY a slot it actually emptied.
            // A code the remap REPORTS dropped but that still holds a survivor
            // (the mixed slot) is alive: stamping `cleared` there would claim
            // the slot references nothing (R8) about a slot that does. Filler
            // and filer writes re-arm every touched slot by design.
            if (write === 'case_change_clear' && holdsReference(nextValues[code])) continue;
            stamped[code] = nextAssignmentProvenance({
              // `current` is deliberately unread by the decider — a write must
              // not choose its starting point — recorded here for the trace.
              current: getAssignmentProvenance(d, code),
              write,
            });
            stampedAny = true;
          }
          // Absence IS "nothing recorded" (the sectionStatuses discipline):
          // write the key only when something was actually stamped, and merge
          // into the existing map so a write of one code never disturbs
          // another's recorded provenance.
          if (!stampedAny) return { ...d, metadataValues: nextValues };
          return {
            ...d,
            metadataValues: nextValues,
            assignmentProvenance: { ...d.assignmentProvenance, ...stamped },
          };
        }),
      }));
    },
    [],
  );

  const updateCourtFees = useCallback((updates: Partial<CourtFeesState>) => {
    // The record-invalidation effect below applies the cleared patch through
    // this writer — a deliberate state write on a genuine input change, not a
    // render-phase write (the fingerprint baseline is what decides).
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setWizardState(prev => ({
      ...prev,
      courtFees: { ...prev.courtFees, ...updates },
    }));
  }, []);

  // ── Fee-quote record invalidation (SPEC-SOR-363) ──
  //
  // The Review Fees step writes the fee record from its calculation, but the
  // inputs that quote come from other steps. Without this effect a resolved
  // record outlives its inputs — and because `paymentMethodRequired` keys the
  // card requirement on that record, a stale resolved $0.00 waives the card on
  // a filing that genuinely costs money (the Submit-on-a-stale-quote repro).
  // It lives here, on the always-mounted provider, and NOT in CourtFeesTab:
  // the repro reaches Submit without ever remounting that tab, so a tab-local
  // effect can never observe the change. Mirrors the court-change discipline
  // (reset-court-scoped-case.ts): the transition is CHECKED against a
  // fingerprint baseline, never assumed from the fact that a render happened.
  const feeInputsRef = useRef<string | null>(null);
  // Hydration writers (resume / edit / fresh start) replace the whole draft
  // AFTER mount — `wizardState` initializes from `defaultWizardState`, and
  // `loadDraft`/`loadDraftFromServer` run later — so the fingerprint they
  // produce is not an input change to react to but the new baseline. Arming
  // this flag makes the next observation re-baseline instead of clearing the
  // very record the hydration just restored.
  const feeInputsRebaselineRef = useRef(false);
  const feeInputs = useMemo(() => feeInputFingerprint(wizardState), [wizardState]);

  useEffect(() => {
    const previous = feeInputsRef.current;
    feeInputsRef.current = feeInputs;
    // First observation: establish the baseline. Also the hydration path — a
    // writer that armed the flag vouches for its own state.
    if (previous === null || feeInputsRebaselineRef.current) {
      feeInputsRebaselineRef.current = false;
      return;
    }
    if (previous === feeInputs) return;
    // Whether a record is there to invalidate is this caller's gate: a
    // failed/degraded/unresolved face and its retry control are nothing this
    // patch may clobber — only a RESOLVED record is stale-able.
    const patch = feeQuoteInvalidationPatch(previous, feeInputs);
    if (patch && isFeeQuoteResolved(wizardState.courtFees)) {
      updateCourtFees(patch);
    }
  }, [feeInputs, wizardState.courtFees, updateCourtFees]);

  // The live read side of that baseline: the in-flight fee-calc arrival in
  // CourtFeesTab compares the fingerprint its request fired under against
  // THIS, so an answer priced under inputs the filer has since changed is
  // dropped whole instead of resurrecting the record the effect just cleared.
  // It reads the ref at call time, on the provider that never unmounts — the
  // navigation that changed the input unmounts the tab, where a
  // component-local mirror would have frozen at its last render and let the
  // stale answer land.
  const getFeeInputsFingerprint = useCallback(() => feeInputsRef.current, []);

  const updateOrderDetails = useCallback((updates: Partial<OrderDetailsState>) => {
    setWizardState(prev => ({
      ...prev,
      orderDetails: { ...prev.orderDetails, ...updates },
    }));
  }, []);

  // Per-doc-type required metadata items, published by DocumentsTab from its
  // policy fetch. Policy data isn't wizard state, so it lives here (not in the
  // draft) purely to let validation enforce required metadata (R6).
  const [docTypeMetadata, setDocTypeMetadata] = useState<Record<string, DocumentMetadataItem[]>>({});
  const registerDocTypeMetadata = useCallback((map: Record<string, DocumentMetadataItem[]>) => {
    setDocTypeMetadata(map);
  }, []);

  // SPEC-SOR-174 R9 — the court-scoped reference data the readiness predicates
  // consult, derived from the same policy publication above so the section
  // gate's wording and the confirm control's enabled state read one value.
  const sectionReadinessOptions = useMemo(
    () => buildSectionReadinessOptions(docTypeMetadata),
    [docTypeMetadata],
  );

  // Validation — delegates to shared pure functions in order-logic.
  // Documents is the first tab in the document-first flow, so it precedes both
  // the case info that unlocks the document-type list and the roster that
  // party-class metadata references. Each requirement is deferred ONLY while it
  // is genuinely unassignable (Walter 2026-07-10 R1/R5/R6): the moment the
  // case type resolves, a type-less document is a hard error that blocks Next
  // (previously the deferral never expired, and a type-less document sailed
  // through to fee-calc and bounced as a raw JTI 4112 fault); likewise required
  // party metadata once a roster exists.
  // In the confirm flow, party-class metadata on Upload ALWAYS defers
  // (WOL-419 / Walter R68/R69): participants are created on the Confirm step,
  // one step AFTER Upload, so a roster minted by extraction must not arm hard
  // errors the filer cannot satisfy without leaving the step — that deadlocked
  // the wizard (Next disabled, Confirm unreachable). Strict enforcement still
  // happens at the fee-calc gate (getFeeCalcBlockers), which fires on the
  // Confirm step where Case Participants + Document Assignments live.
  const validateTab = useCallback((tab: EFilingTab): boolean => {
    return sharedValidateTab(wizardState, tab, {
      showCourtFeesTab,
      deferPartyAssignment:
        tab === 'documents' && (confirmFlowEnabled || !canAssignPartyMetadata(wizardState)),
      deferDocTypeAssignment: tab === 'documents' && !canAssignDocTypes(wizardState),
      // R90 (WOL-442, confirm flow): court + case category/type + doc types
      // are HARD Upload requirements — extraction reading nothing must not
      // let an empty filing advance (detection = accelerator, not the gate).
      requireUploadFoundation: tab === 'documents' && confirmFlowEnabled,
      // R10: each step's Next additionally waits on the guided sections that
      // step owns. Threaded on EVERY tab rather than only the two that own
      // sections — the owner map inside the validator decides which tabs the
      // rules apply to, and narrowing it here would be a second copy of AX3.
      confirmFlowEnabled,
      // R9 — the court/case-scoped document-type list travels with the policy
      // metadata it comes from, through one builder both this gate and the
      // confirm control read (see `buildSectionReadinessOptions`).
      ...sectionReadinessOptions,
    }).valid;
  }, [wizardState, showCourtFeesTab, sectionReadinessOptions, confirmFlowEnabled]);

  const getTabValidationErrors = useCallback((tab: EFilingTab): ValidationError[] => {
    return sharedValidateTab(wizardState, tab, {
      showCourtFeesTab,
      deferPartyAssignment:
        tab === 'documents' && (confirmFlowEnabled || !canAssignPartyMetadata(wizardState)),
      deferDocTypeAssignment: tab === 'documents' && !canAssignDocTypes(wizardState),
      requireUploadFoundation: tab === 'documents' && confirmFlowEnabled,
      confirmFlowEnabled,
      ...sectionReadinessOptions,
    }).errors;
  }, [wizardState, showCourtFeesTab, sectionReadinessOptions, confirmFlowEnabled]);

  // Strict fee-calc readiness (R7) — no deferrals. The wizard's Next gate into
  // Review Fees and CourtFeesTab's own guard both consume this, so an
  // incomplete envelope can never initiate fee calculation.
  const getFeeCalcBlockers = useCallback((): ValidationError[] => {
    return validateFeeCalcReadiness(wizardState, { metadataByDocType: docTypeMetadata }).errors;
  }, [wizardState, docTypeMetadata]);

  // R8 (SPEC-SOR-321): the validation banner's items are the canonical id
  // space, so the pair is derived from the SAME read the banner renders — both
  // sides recompute from state on every render, which is what makes "returns to
  // valid when no item remains" hold by construction.
  const offendingFieldAria = useCallback(
    (controlId: string): OffendingFieldAria =>
      computeOffendingFieldAria(controlId, getTabValidationErrors(currentTab), wizardState),
    [getTabValidationErrors, currentTab, wizardState],
  );

  const tabValidation: TabValidation = {
    'order-info': validateTab('order-info'),
    'case-details': validateTab('case-details'),
    'confirm': validateTab('confirm'),
    'documents': validateTab('documents'),
    'court-fees': validateTab('court-fees'),
    // SPEC-SOR-363: the R19 cutover left the shared validator's
    // `order-details` arm unconditionally valid — the card rule moved to
    // `validatePaymentMethod`, composed into `validateEFilingOrder` only. The
    // stepper reads THIS map as isComplete/isClickable, so without composing
    // the rule back in here, Submit rendered COMPLETE and clickable on a
    // brand-new empty draft. Consumer-side composition — the shard for the
    // r19 rule pins the validator's own arms, so they stay as shipped.
    'order-details':
      validateTab('order-details') &&
      validatePaymentMethod(wizardState.courtFees, wizardState.orderDetails).valid,
  };

  // Completeness — calculated from wizard state, memoized for performance
  const completeness = useMemo(
    () => calculateCompleteness(wizardState),
    [wizardState],
  );

  const canProceed = useCallback((tab: EFilingTab): boolean => {
    return validateTab(tab);
  }, [validateTab]);

  const getVisibleTabs = useCallback((): EFilingTab[] => {
    if (!showCourtFeesTab) {
      return tabOrder.filter(t => t !== 'court-fees');
    }
    return tabOrder;
  }, [showCourtFeesTab, tabOrder]);

  const nextTab = useCallback(() => {
    const visibleTabs = getVisibleTabs();
    const currentIndex = visibleTabs.indexOf(currentTab);
    if (currentIndex < visibleTabs.length - 1) {
      setCurrentTab(visibleTabs[currentIndex + 1]);
    }
  }, [currentTab, getVisibleTabs]);

  const previousTab = useCallback(() => {
    const visibleTabs = getVisibleTabs();
    const currentIndex = visibleTabs.indexOf(currentTab);
    if (currentIndex > 0) {
      setCurrentTab(visibleTabs[currentIndex - 1]);
    }
  }, [currentTab, getVisibleTabs]);

  // ── API Integration ──

  // SOR-155: the wizard only ever persists e-filing orders. A draft hydrated
  // from a legacy Federal order may still carry orderInfo.isFederal; strip it
  // from the persisted body so the order can never be created or submitted
  // carrying isFederal:true and contradicting orderType:'e-filing' (AC3).
  const buildPersistedTypeData = useCallback(() => {
    const { isFederal: _isFederal, ...orderInfo } = wizardState.orderInfo;
    return { orderType: effectiveOrderType, ...wizardState, orderInfo };
  }, [wizardState, effectiveOrderType]);

  // Both primitives record the save outcome (SOR-355) — they are the ONE
  // chokepoint every save path funnels through (the autosave effect, the
  // wizard's Save As Draft button, SummaryV2's Save Draft button, and the
  // submit/upload flows), so recording here covers every path without editing
  // the handlers' calls. `savedRef` is the closure's `wizardState` — literally
  // the object `buildPersistedTypeData` serialized — so the indicator reads
  // saved until a writer replaces that reference.
  const createDraftOrder = useCallback(async (): Promise<string> => {
    const savedRef = wizardState;
    const seq = ++saveSeqRef.current;
    try {
      const result = await post<TypedOrder>(`${API_BASE_URL}/api/orders`, {
        type: effectiveOrderType,
        typeData: buildPersistedTypeData(),
      });
      setOrderId(result.id);
      recordSave(seq, 'succeeded', savedRef);
      return result.id;
    } catch (err) {
      recordSave(seq, 'failed', savedRef);
      throw err;
    }
  }, [post, effectiveOrderType, buildPersistedTypeData, wizardState, recordSave]);

  const saveDraftToServer = useCallback(async (): Promise<void> => {
    // Early-return BEFORE recording: a no-op with no order to save must not
    // stamp a save that never happened.
    if (!orderId) return;
    const savedRef = wizardState;
    const seq = ++saveSeqRef.current;
    try {
      await patch(`${API_BASE_URL}/api/orders/${orderId}`, {
        type: effectiveOrderType,
        typeData: buildPersistedTypeData(),
      });
      recordSave(seq, 'succeeded', savedRef);
    } catch (err) {
      recordSave(seq, 'failed', savedRef);
      throw err;
    }
  }, [patch, orderId, effectiveOrderType, buildPersistedTypeData, wizardState, recordSave]);

  const submitOrderToApi = useCallback(async (): Promise<TypedOrder> => {
    let id: string | null = orderId;
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitOutcomeUnknown(false);
    try {
      if (!id) {
        id = await createDraftOrder();
      } else {
        // Ensure latest state is saved before submitting
        await saveDraftToServer();
      }
      const result = await post<TypedOrder>(`${API_BASE_URL}/api/orders/${id}/submit`);
      return result;
    } catch (err) {
      // TypeError signals a network-level failure (no HTTP response — connection reset,
      // abort, status_code: 0). The server may have committed. Reconcile against the
      // authoritative order state before rendering any view.
      if (err instanceof TypeError && id !== null) {
        // Bounded retry window: an immediate GET can read `draft` before the
        // server transaction commits, and the reconcile itself can fail while
        // the network is still flaky. Only a `draft` observed on the FINAL
        // attempt concludes rollback.
        let confirmedDraft = false;
        for (let attempt = 1; attempt <= RECONCILE_MAX_ATTEMPTS; attempt++) {
          try {
            const reconciled = await get<TypedOrder>(`${API_BASE_URL}/api/orders/${id}`);
            if (isSettledSubmitStatus(reconciled.status)) {
              return reconciled;
            }
            confirmedDraft = true;
          } catch {
            confirmedDraft = false;
          }
          if (attempt < RECONCILE_MAX_ATTEMPTS) {
            await delay(reconcileRetryDelayMs);
          }
        }
        if (confirmedDraft) {
          // Reconcile confirmed the order is still draft — the server rolled
          // back, so offering retry is safe.
          setSubmitError(err.message);
        } else {
          // The reconcile never reached the server: the outcome is still
          // unknown. Do NOT re-enable Submit — a second submit while the
          // first may have landed could double-file.
          setSubmitOutcomeUnknown(true);
        }
        throw err;
      }
      const message = err instanceof Error ? err.message : 'Submission failed';
      // SPEC-SOR-424 R13/R16 — the two refusal classes are disjoint, and the
      // clear-or-not decision IS the wizard's handling of each:
      //   'requote' — the server refused because the order's quote is missing,
      //     stale, expired or degraded, so the figures on screen are not the
      //     ones it would honor. Clearing the record to the absent state is the
      //     existing cleared-record path (`clearedFeeRecordPatch('absent')` —
      //     the same patch a changed-input invalidation writes), and the clear
      //     IS the resubmit block, structurally: `courtFees.quoteState` is a
      //     completeness field and `validatePaymentMethod` raises
      //     `fee-quote-not-resolved` on an unresolved record, so no second
      //     submit can be issued until a fresh calculation has landed and
      //     re-rendered. The server's own message carries the re-quote
      //     instruction, so nothing is composed client-side.
      //   'payment' — the quote is current and the order simply carries no
      //     stored card. Nothing here touches the RECORD: it survives resolved,
      //     so the figures stay on screen and the message says nothing about
      //     re-quoting. What it clears is the CLIENT's card
      //     (`orderDetails.paymentMethodId`), because that field is the
      //     checklist's own `hasPaymentMethod` and it is a copy of a fact the
      //     server has just answered: an order whose stored method is gone can
      //     still hold a local card (the wizard saved one, the order row never
      //     got it), and leaving it standing renders "Card on file — charged
      //     $X on submit" and a DONE payment row beside the server's message
      //     saying the order needs one. Dropping it is the R8 subordination —
      //     the client gate follows the server's answer — and it is what puts
      //     the add-a-card demand back in front of the filer.
      // Any other failure keeps today's path exactly.
      const refusalClass = classifySubmitRefusal(err);
      if (refusalClass === 'requote') {
        updateCourtFees(clearedFeeRecordPatch('absent'));
      } else if (refusalClass === 'payment') {
        updateOrderDetails({ paymentMethodId: '' });
      }
      setSubmitError(message);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [orderId, createDraftOrder, saveDraftToServer, post, get, reconcileRetryDelayMs, updateCourtFees, updateOrderDetails]);

  const uploadDocument = useCallback(async (file: File, category?: string): Promise<OrderDocument> => {
    let id = orderId;
    if (!id) {
      id = await createDraftOrder();
    }

    // Track upload status by filename (docId isn't known here, matched in DocumentsTab)
    const fileKey = file.name;
    pendingFilesRef.current.set(fileKey, file);
    setUploadStatuses(prev => ({ ...prev, [fileKey]: 'uploading' }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (category) formData.append('category', category);

      const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/${id}/documents`, {
        method: 'POST',
        body: formData,
        headers: {}, // Let browser set Content-Type with boundary
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(error.message || error.error || 'Upload failed');
      }
      setUploadStatuses(prev => ({ ...prev, [fileKey]: 'uploaded' }));
      return response.json();
    } catch (err) {
      setUploadStatuses(prev => ({ ...prev, [fileKey]: 'failed' }));
      throw err;
    }
  }, [orderId, createDraftOrder, fetchWithAuth]);

  const retryUpload = useCallback(async (docFileName: string, file: File) => {
    setUploadStatuses(prev => ({ ...prev, [docFileName]: 'uploading' }));
    try {
      await uploadDocument(file);
    } catch {
      // Status already set to 'failed' in uploadDocument
    }
  }, [uploadDocument]);

  const allUploadsComplete = useMemo(() => {
    const statuses = Object.values(uploadStatuses);
    if (statuses.length === 0) return true;
    return statuses.every(s => s === 'uploaded');
  }, [uploadStatuses]);

  // ── Reference-data prefetch ──
  // The moment the county is known — court select on "Where to File", an
  // OCR-derived county, or a subsequent-filing case load — warm the server's
  // court policy. The first /reference/categories call forces a full GetPolicy
  // and populates the 24h SWR cache, so the Case Details step's categories /
  // types / case-fields / court-jurisdiction calls return from cache instead of
  // paying a multi-second cold GetPolicy. We also stash the categories so
  // CaseInfoTab can render them without a loading flash. Best-effort: failures
  // are swallowed and re-attempted by CaseInfoTab's own fetch.
  const prefetchedCountiesRef = useRef<Set<string>>(new Set());
  const [prefetchedCategories, setPrefetchedCategories] = useState<Record<string, RefOption[]>>({});

  useEffect(() => {
    const county = wizardState.orderInfo.county;
    if (!county || !isAuthenticated || prefetchedCountiesRef.current.has(county)) return;
    prefetchedCountiesRef.current.add(county);
    fetchWithAuth(`${API_BASE_URL}/api/efiling/reference/categories?county=${encodeURIComponent(county)}`)
      .then(res => (res.ok ? res.json() : null))
      .then(json => {
        if (json?.data) setPrefetchedCategories(prev => ({ ...prev, [county]: json.data }));
      })
      .catch(() => {
        // Allow a later retry (e.g. transient network) by un-marking the county.
        prefetchedCountiesRef.current.delete(county);
      });
  }, [wizardState.orderInfo.county, isAuthenticated, fetchWithAuth]);

  // ── OCR Extraction ──

  const applyExtraction = useCallback((patch: ExtractedPatch) => {
    const populated = new Set<string>();

    setWizardState(prev => {
      const orderUpdates: Partial<OrderInfoState> = {};
      const caseUpdates: Partial<CaseInfoState> = {};

      if (patch.court?.countyCode) {
        orderUpdates.county = patch.court.countyCode;
        populated.add('county');
        // WOL-341: county is the canonical court source; courtJurisdiction is a
        // sub-location *within* it, and its option list is county-scoped. If OCR
        // changes the county, any previously-chosen courtJurisdiction is now stale
        // (it belongs to the old county) — clear it so CaseInfoTab re-resolves the
        // location for the new county. Leaving both stranded persists two
        // conflicting court fields and routes the filing to the wrong CaseCourt id.
        if (patch.court.countyCode !== prev.orderInfo.county && prev.caseInfo.courtJurisdiction) {
          caseUpdates.courtJurisdiction = null;
        }
      }

      if (confirmFlowEnabled) {
        // R88 (Walter, WOL-443): extraction must never clobber the filer's
        // explicit filing-type choice — his "Existing case" pick was flipping
        // back to "New case" whenever a later scan read nothing. Two rules:
        //  1. A detected case number is evidence of an existing case — adopt
        //     it while the field is unset or was itself extraction-set (prev
        //     ocrPopulatedFields carries 'filingType'), never over a manual
        //     pick. Stash the number so the filing-type banner and the case
        //     lookup can show it.
        //  2. NO case number only ever INFERS "new case" while the field is
        //     unset (the fresh-upload flow keeps pre-filling); a blank scan
        //     never downgrades ANY existing value — that was the clobber.
        const filingTypeWasOcrSet = (prev.ocrPopulatedFields ?? []).includes('filingType');
        if (patch.caseNumber && (!prev.orderInfo.filingType || filingTypeWasOcrSet)) {
          orderUpdates.filingType = 'subsequent-filing';
          populated.add('filingType');
          if (!prev.caseInfo.caseTrackingId) {
            caseUpdates.trialCourtCaseNumber = patch.caseNumber;
          }
        } else if (!patch.caseNumber && !prev.orderInfo.filingType) {
          orderUpdates.filingType = 'case-initiation';
          populated.add('filingType');
        }
      } else {
        // Classic flow: unchanged until the post-session shared-surface update
        // (same discipline as R72/R80 — Thursday's surface must not move).
        if (patch.caseNumber) {
          orderUpdates.filingType = 'subsequent-filing';
        } else {
          orderUpdates.filingType = 'case-initiation';
        }
        populated.add('filingType');
      }

      if (patch.caption?.full) {
        caseUpdates.caseTitle = patch.caption.full;
        populated.add('caseTitle');
      }

      if (patch.incidentZip) {
        caseUpdates.incidentZip = patch.incidentZip;
        populated.add('incidentZip');
      }

      // SOR-173: routing the extracted case category by whether it resolved to
      // one of the court's own codes is ONE rule, shared with the server's
      // applyExtraction rather than hand-mirrored into it — the two copies had
      // already drifted (the server dropped the candidate set entirely).
      const categoryRouting = routeExtractedCaseCategory(patch.caseCategory);
      Object.assign(caseUpdates, categoryRouting.updates);
      for (const field of categoryRouting.populatedFields) populated.add(field);

      // SOR-194: the case TYPE goes through the same shared rule, for the same
      // reason. SOR-173 unified the category and left this branch as the second
      // hand-mirrored copy, writing a court-rejected name into `caseType.name`
      // — which the confirm card and the Filing Summary both render — beside
      // the empty code that makes the picker say we couldn't read it.
      const typeRouting = routeExtractedCaseType(patch.caseType);
      Object.assign(caseUpdates, typeRouting.updates);
      for (const field of typeRouting.populatedFields) populated.add(field);

      // SOR-223: merge rather than replace, so an applied extraction preserves
      // every existing case-roster/manual entry and a re-extraction never
      // multiplies them. The minting + source-attributed dedup lives in the pure
      // merge lib. populated flags stay gated on the patch carrying a roster — an
      // omitted roster leaves both the arrays (the helper returns them unchanged)
      // and the confirm-card display untouched (R2).
      const newParties = mergeExtractedParties(prev.parties, patch.parties);
      if (patch.parties?.length) populated.add('parties');

      const newAttorneys = mergeExtractedAttorneys(prev.attorneys, patch.attorneys);
      if (patch.attorneys?.length) populated.add('attorneys');

      // SPEC-SOR-174 R19/R20/R27: everything above ran unchanged — this is the
      // single chokepoint that decides what may land. A section the filer has
      // already confirmed keeps every field it had (R20); a touched pending
      // section becomes prefilled (R19); nothing here can reach `confirmed`
      // (R27). Inert in the classic flow (R16).
      const guarded = guardExtractionSections({
        confirmFlowEnabled,
        statuses: prev.sectionStatuses,
        orderUpdates,
        caseUpdates,
        rosterTouched: Boolean(patch.parties?.length || patch.attorneys?.length),
        populated,
      });

      return {
        ...prev,
        orderInfo: { ...prev.orderInfo, ...guarded.orderUpdates },
        caseInfo: { ...prev.caseInfo, ...guarded.caseUpdates },
        parties: guarded.applyRoster ? newParties : prev.parties,
        attorneys: guarded.applyRoster ? newAttorneys : prev.attorneys,
        // Persist with the draft so the confirmation cards rehydrate on reload.
        ocrPopulatedFields: Array.from(guarded.populated),
        sectionStatuses: guarded.sectionStatuses,
      };
    });

    setExtractionStatus('extracted');
  }, [confirmFlowEnabled]);

  // SPEC-SOR-174 R30: the unbound default FOR THE ACTIVE FLOW. Stamping it here
  // rather than after the fact means the slot `startFreshDraft` writes
  // synchronously is already the deterministic default a resume will read —
  // there is no window in which the persisted draft claims to be a classic one.
  const freshWizardState = useCallback(
    (): EFilingWizardState =>
      confirmFlowEnabled ? { ...defaultWizardState, confirmFlowActive: true } : defaultWizardState,
    [confirmFlowEnabled],
  );

  /**
   * The single normalization every hydrated draft passes through, whichever
   * path produced it — the localStorage resume or the server `type_data` load.
   * Both used to spell these steps out separately, which is how a normalization
   * added later ends up applying to only one of them.
   *
   * SPEC-SOR-174 R30 is the newest step: the confirm flow is a per-browser
   * client setting, so a draft that does not carry `confirmFlowActive` reads
   * server-side as a classic one, and `assessReadiness` — which sees state and
   * nothing else — would mark a zero-confirmation draft ready for review. A
   * legacy draft resumed in the confirm flow therefore gets stamped here, and
   * the wizard's own save persists it.
   *
   * The stamp tracks the flow the draft is being WORKED IN, so hydrating under
   * the classic opt-out clears it. Stamping only ever ON would strand a draft
   * started in the confirm flow and resumed under `?confirmFlow=0`: the classic
   * wizard renders no confirm controls, so no section can reach `confirmed`,
   * while the flag keeps `validateEFilingOrder` refusing the order — a draft the
   * server readiness path can never pass and the filer has no in-flow way to
   * fix. Clearing it hands the draft to the classic rules whole, which is what
   * the opt-out means. It opens no hole: the foreign-court refusal (R25) is
   * flow-INDEPENDENT, so Andrew's Madera-under-Marin repro is still full stop in
   * classic, and only the confirm-flow-scoped legs (R29's absent binding, R30's
   * section gate) relax — exactly the legs R16 says classic never had. Nor is it
   * a bypass: the client Submit gate already reads the runtime flag, and a
   * `?confirmFlow=1` visit re-stamps on the next hydration.
   *
   * A court change and a case change still do NOT clear it — neither changes
   * which flow the filer is in. A flow switch is the one thing that does.
   */
  const normalizeHydratedState = useCallback(
    (state: EFilingWizardState): EFilingWizardState => {
      state.caseInfo = { ...defaultCaseInfo, ...state.caseInfo };
      applyCategoryBackfill(state);
      applyCaseTypeBackfill(state);
      // SOR-223: backfill `source` onto legacy untagged entries so a
      // pre-change draft hydrates deterministically to 'manual' (a present tag
      // is preserved). Arrays may be absent on very old drafts.
      state.parties = withDefaultSource(state.parties ?? []);
      state.attorneys = withDefaultSource(state.attorneys ?? []);
      // SPEC-SOR-220-v2: backfill a legacy document's provenance the same way.
      // A slot holding a reference with no recorded tag is defensively read
      // `filer_chosen` (the reference is exactly what the filer's own
      // document-level pick produced) and MATERIALIZED as that tag, so the
      // reading is stable across every later load. Hydration never
      // re-litigates a change it did not observe: a present tag survives
      // verbatim and nothing is stamped where the reading is `untouched` —
      // `untouched` is read out of absence, never written.
      if (state.documents) {
        state.documents = state.documents.map(doc => {
          const backfilled: AssignmentProvenanceMap = {};
          let backfilledAny = false;
          for (const code of PARTY_CLASS_CODES) {
            if (doc.assignmentProvenance?.[code] !== undefined) continue;
            if (getAssignmentProvenance(doc, code) !== 'filer_chosen') continue;
            backfilled[code] = 'filer_chosen';
            backfilledAny = true;
          }
          // Merge — never replace: an existing tag (e.g. FILING_PARTY already
          // `cleared`) must survive the backfill of a sibling code.
          return backfilledAny
            ? { ...doc, assignmentProvenance: { ...doc.assignmentProvenance, ...backfilled } }
            : doc;
        });
      }
      if (confirmFlowEnabled) state.confirmFlowActive = true;
      else delete state.confirmFlowActive;
      return state;
    },
    [confirmFlowEnabled],
  );

  // Draft persistence
  const saveDraft = useCallback((): boolean => {
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ wizardState, orderId }));
      return true;
    } catch {
      // localStorage may be unavailable (storage disabled, quota exceeded) —
      // reported, not asserted away: on a path where this mirror is the WHOLE
      // save, claiming success here would print "Saved" for an unpersisted
      // draft (SOR-355 review).
      return false;
    }
  }, [wizardState, orderId]);

  const loadDraft = useCallback((): boolean => {
    try {
      const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Support both old format (bare state) and new format ({ wizardState, orderId })
        const state: EFilingWizardState = parsed.wizardState ?? parsed;
        const savedOrderId: string | null = parsed.orderId ?? null;
        const normalized = normalizeHydratedState(state);
        setWizardState(normalized);
        // Hydration replaces the whole draft after mount: the next fingerprint
        // observation is the new baseline, not an input change to react to.
        feeInputsRebaselineRef.current = true;
        if (savedOrderId) {
          setOrderId(savedOrderId);
        }
        // SOR-355, implementer judgment per the issue notes: a resumed draft
        // was persisted by an earlier successful save, so it reads saved
        // immediately instead of flashing "Not saved" until the first
        // autosave tick.
        recordSave(++saveSeqRef.current, 'succeeded', normalized);
        return true;
      }
    } catch {
      // Ignore parse errors
    }
    return false;
  }, [normalizeHydratedState, recordSave]);

  const loadDraftFromServer = useCallback(async (draftOrderId: string): Promise<boolean> => {
    const order = await get<TypedOrder>(`${API_BASE_URL}/api/orders/${draftOrderId}`);
    // Draft orders resume for editing; ready-for-review orders hydrate for the
    // markReadyForReview review deep-link (/eFiling?orderId=). Any other status is
    // read-only. Suppress the post-load auto-save so merely opening a
    // ready-for-review order to review it doesn't reopen it to draft — the reopen
    // (R-EDIT-REOPEN) must follow a genuine user edit, not hydration.
    if ((order.status !== 'draft' && order.status !== 'ready-for-review') || !order.typeData) return false;
    skipNextServerSaveRef.current = true;
    // The mirrored localStorage write below persists whatever the shared
    // normalization backfilled, so both hydration paths land in one shape.
    const state = normalizeHydratedState({
      ...defaultWizardState,
      ...(order.typeData as unknown as Partial<EFilingWizardState>),
    });
    setWizardState(state);
    setOrderId(order.id);
    // SOR-375: a listed document with no stored file behind it never reads as
    // done. The verdict is the ONE shared one — every order_documents row for
    // this order whose gcs_path is present backs its fileName — and a phantom
    // entry is stamped failed so its card shows the red "Upload failed" state
    // and allUploadsComplete (the submit gate) stays false. The server strips
    // these entries on its next write of this order (rejectedDocuments), so
    // the stamp describes the draft until the filer re-uploads.
    const unbacked = unbackedListedFileNames(
      listedFileNames(state.documents),
      backedFileNames(order.documents ?? []),
    );
    if (unbacked.length > 0) {
      setUploadStatuses(prev => {
        const next = { ...prev };
        for (const fileName of unbacked) next[fileName] = 'failed';
        return next;
      });
    }
    // Hydration replaces the whole draft after mount: the next fingerprint
    // observation is the new baseline, not an input change to react to.
    feeInputsRebaselineRef.current = true;
    // SOR-355, implementer judgment per the issue notes: a hydrated draft is
    // a previously-persisted one — record the hydrated reference as saved so
    // an edit-mode draft reads saved immediately. The suppressed autosave
    // (skipNextServerSaveRef) does not contradict this: the draft IS persisted.
    // The sequence is stamped HERE, after the fetch resolves: the record must
    // land on top of any save recorded while the GET was in flight (those
    // describe the pre-hydration state and must lose to it), while a server
    // save that started earlier but settles later is dropped as stale.
    recordSave(++saveSeqRef.current, 'succeeded', state);
    try {
      // Mirror to localStorage so reloads and the Auth0 redirect round-trip keep working.
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ wizardState: state, orderId: order.id }));
    } catch {
      // localStorage may be unavailable
    }
    return true;
  }, [get, normalizeHydratedState, recordSave]);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    } catch {
      // Ignore
    }
    setWizardState(freshWizardState());
    setOrderId(null);
    // A fresh draft is default state end to end (courtFees reads `absent`), so
    // the next fingerprint observation baselines rather than invalidates.
    feeInputsRebaselineRef.current = true;
    setSubmitError(null);
    setSubmitOutcomeUnknown(false);
    setUploadStatuses({});
    setDocTypeMetadata({});
    setCurrentTab('documents');
    // The state the last save persisted was just discarded (SOR-355): nothing
    // is saved about the fresh draft until the next save attempt records. The
    // reset takes the newest sequence position, so an in-flight save for the
    // discarded draft cannot settle afterwards and resurrect a saved claim
    // about state that no longer exists.
    recordSave(++saveSeqRef.current, 'none', null);
    // Reset OCR extraction status. defaultWizardState already clears
    // ocrPopulatedFields/ocrConfirmedFields, but extractionStatus is standalone
    // component state — without this it stays 'extracted'/'failed' from the prior
    // order, and DocumentsTab's `extractionStatus === 'idle'` guard then silently
    // skips OCR on the next upload (no auto-fill on a 2nd order in the same
    // session unless the page is reloaded).
    setExtractionStatus('idle');
  }, [freshWizardState, recordSave]);

  const startFreshDraft = useCallback(() => {
    // Reuse clearDraft's full in-memory reset (freshWizardState, null orderId,
    // cleared submitError/uploadStatuses, currentTab, extractionStatus) so the
    // reset never drifts. clearDraft only removeItem()s the slot, leaving it empty
    // until the 1s auto-save re-persists; overwrite it synchronously here so the
    // stale draft cannot be resumed before the next page load (R2) and the slot is
    // deterministically the default unbound state.
    clearDraft();
    try {
      localStorage.setItem(
        DRAFT_STORAGE_KEY,
        JSON.stringify({ wizardState: freshWizardState(), orderId: null }),
      );
    } catch {
      // localStorage may be unavailable
    }
  }, [clearDraft, freshWizardState]);

  // Auto-save draft on state changes (localStorage + server when orderId exists)
  useEffect(() => {
    // The state this tick would persist, captured at effect time: a state
    // change before the debounce elapses re-runs the effect (and clears the
    // timer), so this is always the reference `saveDraft()` serializes.
    const savedRef = wizardState;
    const timer = setTimeout(() => {
      // localStorage may refuse the write, and on the non-server legs below it
      // is the WHOLE save — record what actually happened, never an
      // unconditional success (SOR-355 review). These legs are synchronous, so
      // their sequence position is stamped as they record; only the async
      // primitives stamp at start.
      const persisted = saveDraft();
      if (orderId && isAuthenticated && !savePendingRef.current) {
        if (skipNextServerSaveRef.current) {
          // Consume the post-hydration cycle without a server write; the next
          // real edit saves (and reopens a ready-for-review order to draft).
          // The localStorage leg above is this cycle's whole save, so its
          // outcome is the one recorded.
          skipNextServerSaveRef.current = false;
          recordSave(++saveSeqRef.current, persisted ? 'succeeded' : 'failed', savedRef);
        } else {
          savePendingRef.current = true;
          // saveDraftToServer records its own outcome — it is the recording
          // chokepoint for every server save path (autosave, either Save
          // Draft button, submit) — so this callback only clears the guard
          // and keeps a server failure from being an unhandled rejection.
          saveDraftToServer()
            .finally(() => {
              savePendingRef.current = false;
            })
            .catch(() => {
              // Outcome already recorded by saveDraftToServer.
            });
        }
      } else if (!savePendingRef.current) {
        // No server leg ran (no order yet, or signed out) and none is in
        // flight: the localStorage write above is the whole save. An in-flight
        // server save still owns the outcome until it settles, so this leg
        // stays silent then — and once it settles, the monotonic sequence
        // drops a completion older than the one already recorded.
        recordSave(++saveSeqRef.current, persisted ? 'succeeded' : 'failed', savedRef);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [wizardState, saveDraft, orderId, isAuthenticated, saveDraftToServer, recordSave]);

  const value: EFilingContextValue = {
    currentTab,
    setCurrentTab,
    wizardState,
    updateOrderInfo,
    updateCaseInfo,
    setParties,
    addParty,
    removeParty,
    updateParty,
    setAttorneys,
    addAttorney,
    removeAttorney,
    setDocuments,
    addDocument,
    removeDocument,
    updateDocument,
    updateDocumentMetadata,
    updateCourtFees,
    getFeeInputsFingerprint,
    updateOrderDetails,
    tabValidation,
    validateTab,
    getTabValidationErrors,
    getFeeCalcBlockers,
    offendingFieldAria,
    registerDocTypeMetadata,
    docTypeMetadata,
    completeness,
    saveDraft,
    loadDraft,
    loadDraftFromServer,
    clearDraft,
    startFreshDraft,
    saveIndicator,
    canProceed,
    nextTab,
    previousTab,
    effectiveOrderType,
    showCourtFeesTab,
    confirmFlowEnabled,
    orderId,
    isSubmitting,
    submitError,
    submitOutcomeUnknown,
    createDraftOrder,
    saveDraftToServer,
    submitOrder: submitOrderToApi,
    uploadDocument,
    uploadStatuses,
    allUploadsComplete,
    retryUpload,
    extractionStatus,
    setExtractionStatus,
    ocrPopulatedFields,
    applyExtraction,
    confirmOcrField,
    confirmSection,
    resetSectionStatuses,
    revertCaseAttachSections,
    prefetchedCategories,
  };

  return (
    <EFilingContext value={value}>
      {children}
    </EFilingContext>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useEFiling = (): EFilingContextValue => {
  const context = use(EFilingContext);
  if (!context) {
    throw new Error('useEFiling must be used within an EFilingProvider');
  }
  return context;
};

export default EFilingContext;
